import React from "react";
import { useAuth } from "shared/context/auth";
import { useOutletContext, useParams } from "react-router-dom";
import FieldAlias from "shared/form/renderer/enums/FieldAlias";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import { useMyExperienceDashboardDetails } from "apps/myExperience/context/dashboard";
import {
  useCreateOrgAdmin,
  useGetFormByRegistrationParticipant,
  useGetOrgAdminCreateForm,
  useNominateOrgAdmin
} from "./dataloader";
import { CreateOrgAdminService } from "./types";
import { useGetOrganizationDetailsForRegistration } from "shared/form/renderer/components/organizationName/dataloader";
import SchemaService from "shared/form/renderer/SchemaService";
import { ApiRegistrationFormForRegResponse } from "shared/types/ApiRegistrationFormForRegResponse";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsContextEntityTypeEnum, AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export default function useCreateOrgAdminService(): CreateOrgAdminService {
  const formRef = React.useRef<any>();
  const { userDetail } = useAuth();
  const { navigateBack } = useNavigateWrapper();
  const { registrationParticipantId } = useParams();
  const { refetch }: { refetch: () => void } = useOutletContext();
  const { data: dashboardDetails } = useMyExperienceDashboardDetails();
  const isCreateMode = React.useMemo(() => !registrationParticipantId, [registrationParticipantId]);

  useLogAnalyticsScreenHook({
    properties: {
      screenType: isCreateMode ? AnalyticsScreenNameEnum.CREATE_ORG_ADMIN : AnalyticsScreenNameEnum.ASSIGN_ORG_ADMIN,
      contextEntityType: isCreateMode ? undefined : AnalyticsContextEntityTypeEnum.REGISTRATION_PARTICIPANT,
      contextEntityId: registrationParticipantId
    },
    isInternalEvent: true
  });

  const orgAdminFormErrorHandler = React.useCallback(() => navigateBack(), [navigateBack]);

  const {
    data: createOrgAdminForm,
    dataUpdatedAt: createFormUpdatedAt,
    isSuccess: orgAdminFormLoaded
  } = useGetOrgAdminCreateForm({
    registrationId: userDetail?.registrationId,
    errorHandler: orgAdminFormErrorHandler
  });

  const { data: organizationData } = useGetOrganizationDetailsForRegistration({
    registrationFormId: isCreateMode ? createOrgAdminForm?.id : undefined,
    organizationId: dashboardDetails.organizationId
  });

  const { data: nominationForm, dataUpdatedAt: updateFormUpdatedAt } = useGetFormByRegistrationParticipant({
    registrationParticipantId: registrationParticipantId,
    registrationFormId: createOrgAdminForm?.id
  });

  const orgAdminForm = React.useMemo(() => {
    if (isCreateMode && createOrgAdminForm && organizationData) {
      return {
        ...createOrgAdminForm,
        profile: createOrgAdminForm?.profile.map(profile_node => {
          if (profile_node.alias === FieldAlias.ORGANIZATION_NAME) {
            return {
              ...profile_node,
              value: dashboardDetails.registrationOrganizationName,
              disabled: true
            };
          }
          const organizationValue = organizationData?.profileFields.find(obj => obj.id === profile_node.id);
          if (organizationValue) {
            return {
              ...profile_node,
              value: SchemaService.getFieldInitialValue(organizationValue),
              disabled: false
            };
          }

          return profile_node;
        })
      } as ApiRegistrationFormForRegResponse;
    } else if (nominationForm) {
      return nominationForm;
    }
  }, [
    organizationData,
    isCreateMode,
    createOrgAdminForm,
    nominationForm,
    dashboardDetails.registrationOrganizationName
  ]);

  const participantName = React.useMemo(() => {
    return nominationForm ? `${nominationForm.firstName} ${nominationForm.lastName}` : "";
  }, [nominationForm]);

  const organizationId = React.useMemo(() => dashboardDetails.organizationId, [dashboardDetails.organizationId]);

  const orgAdminFormKey = React.useMemo(
    () => createFormUpdatedAt.toString() + updateFormUpdatedAt.toString(),
    [createFormUpdatedAt, updateFormUpdatedAt]
  );

  const { mutateAsync: createOrgAdmin } = useCreateOrgAdmin();
  const { mutateAsync: nominateOrgAdmin } = useNominateOrgAdmin();

  const closeHandler = React.useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const successHandler = React.useCallback(() => {
    navigateBack();
    refetch();
  }, [navigateBack, refetch]);

  const createOrgAdminHandler = React.useCallback(
    values => {
      createOrgAdmin({
        ...values,
        initiatorRegistrationParticipantId: userDetail?.id,
        registrationFormId: createOrgAdminForm?.id,
        organizationId: dashboardDetails.organizationId,
        organizationName: "",
        organizationUrl: ""
      }).then(() => {
        successHandler();
      });
    },
    [createOrgAdmin, userDetail?.id, createOrgAdminForm?.id, dashboardDetails.organizationId, successHandler]
  );

  const nominateOrgAdminHandler = React.useCallback(
    values => {
      nominateOrgAdmin({
        ...values,
        registrationParticipantId,
        initiatorRegistrationParticipantId: userDetail?.id,
        registrationFormId: createOrgAdminForm?.id,
        organizationId: dashboardDetails.organizationId,
        uiVersion: nominationForm?.uiVersion,
        organizationName: "",
        organizationUrl: ""
      }).then(() => {
        successHandler();
      });
    },
    [
      nominateOrgAdmin,
      registrationParticipantId,
      userDetail?.id,
      createOrgAdminForm?.id,
      dashboardDetails.organizationId,
      nominationForm?.uiVersion,
      successHandler
    ]
  );

  const submitHandler = React.useCallback(
    values => {
      if (isCreateMode) {
        createOrgAdminHandler(values);
      } else {
        nominateOrgAdminHandler(values);
      }
    },
    [createOrgAdminHandler, isCreateMode, nominateOrgAdminHandler]
  );

  return {
    isCreateMode,
    registrationParticipantId,
    formRef,
    orgAdminFormKey,
    orgAdminForm,
    submitHandler,
    closeHandler,
    organizationId,
    participantName,
    orgAdminFormLoaded
  };
}
